import { useEffect, useState } from "react";
import { Plus, Trash, Books, ClipboardText, Sparkle } from "@phosphor-icons/react";
import { toast } from "sonner";
import api, { formatApiError } from "../lib/api";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Textarea } from "../components/ui/textarea";
import { Switch } from "../components/ui/switch";
import { Badge } from "../components/ui/badge";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent,
  AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle, AlertDialogTrigger,
} from "../components/ui/alert-dialog";

const THEMES = ["Daily Life", "Relationships", "History & Identity", "Home & Comfort", "Well-being", "Preferences", "Goals & Meaning", "General"];

const SOURCE_STYLES = {
  library: "bg-secondary text-stone-600",
  custom: "bg-primary/10 text-primary",
  ai: "bg-accent text-accent-foreground",
};

export default function QuestionBankPage() {
  const [questions, setQuestions] = useState(null);
  const [text, setText] = useState("");
  const [theme, setTheme] = useState("Daily Life");
  const [bulk, setBulk] = useState("");
  const [busy, setBusy] = useState(false);

  const load = () =>
    api.get("/questions")
      .then((r) => setQuestions(r.data))
      .catch((e) => { toast.error(formatApiError(e)); setQuestions([]); });

  useEffect(() => { load(); }, []);

  const [generating, setGenerating] = useState(false);
  const genAI = async () => {
    setGenerating(true);
    try {
      await api.post("/questions/ai-generate");
      const started = Date.now();
      while (Date.now() - started < 180000) {
        await new Promise((r) => setTimeout(r, 4000));
        const { data } = await api.get("/questions/gen-status");
        if (data.state === "done") {
          toast.success(`AI added ${data.added} questions — toggle on the ones you want`);
          load();
          return;
        }
        if (data.state === "error") {
          toast.error(data.error || "Generation failed. Please retry.");
          return;
        }
      }
      toast.error("Still working — check back in a moment.");
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setGenerating(false);
    }
  };

  const add = async () => {
    if (!text.trim()) return;
    setBusy(true);
    try {
      const { data } = await api.post("/questions", { text: text.trim(), theme });
      setQuestions((qs) => [...(qs || []), data]);
      setText("");
      toast.success("Question added");
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setBusy(false);
    }
  };

  const bulkAdd = async () => {
    if (!bulk.trim()) return;
    setBusy(true);
    try {
      const { data } = await api.post("/questions/bulk", { text: bulk, theme: "General" });
      toast.success(`${data.added} questions added`);
      setBulk("");
      load();
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setBusy(false);
    }
  };

  const toggle = async (q) => {
    try {
      await api.put(`/questions/${q.id}`, { active: !q.active });
      setQuestions((qs) => qs.map((x) => (x.id === q.id ? { ...x, active: !q.active } : x)));
    } catch (e) {
      toast.error(formatApiError(e));
    }
  };

  const remove = async (q) => {
    try {
      await api.delete(`/questions/${q.id}`);
      setQuestions((qs) => qs.filter((x) => x.id !== q.id));
      toast.success("Question deleted");
    } catch (e) {
      toast.error(formatApiError(e));
    }
  };

  const grouped = THEMES.map((t) => ({ theme: t, items: (questions || []).filter((q) => q.theme === t) }))
    .concat((questions || []).filter((q) => !THEMES.includes(q.theme)).length
      ? [{ theme: "Other", items: (questions || []).filter((q) => !THEMES.includes(q.theme)) }]
      : [])
    .filter((g) => g.items.length > 0);

  const activeCount = (questions || []).filter((q) => q.active).length;

  return (
    <div className="max-w-4xl" data-testid="questions-page">
      <h1 className="font-heading text-4xl font-semibold tracking-tight text-stone-900">Question Bank</h1>
      <p className="mt-1 text-sm text-stone-500">
        Interview questions for the Person-Centered Survey. Every question should support our mission: person-centered, holistic, non-medical care that helps people live comfortably at home.
      </p>
      <div className="mt-4">
        <AlertDialog>
          <AlertDialogTrigger asChild>
            <Button variant="outline" disabled={generating} className="border-stone-300 text-stone-700 hover:bg-stone-900 hover:text-white" data-testid="ai-generate-questions-button">
              <Sparkle size={15} className="mr-1.5" weight="fill" /> {generating ? "AI is generating (about a minute)..." : "Generate Question Library (AI)"}
            </Button>
          </AlertDialogTrigger>
          <AlertDialogContent data-testid="ai-gen-dialog">
            <AlertDialogHeader>
              <AlertDialogTitle>Generate a broad question library?</AlertDialogTitle>
              <AlertDialogDescription>
                AI will create ~50 engaging, person-centered questions across all 7 themes — broad enough for any client. They are added as inactive "AI Suggested" questions; toggle on the ones you want available systemwide.
              </AlertDialogDescription>
            </AlertDialogHeader>
            <AlertDialogFooter>
              <AlertDialogCancel data-testid="ai-gen-cancel">Cancel</AlertDialogCancel>
              <AlertDialogAction onClick={genAI} className="bg-primary text-primary-foreground hover:bg-stone-900" data-testid="ai-gen-confirm">Generate</AlertDialogAction>
            </AlertDialogFooter>
          </AlertDialogContent>
        </AlertDialog>
      </div>

      <div className="mt-6 grid grid-cols-1 gap-4 md:grid-cols-2">
        <div className="rounded-lg border border-stone-200 bg-white p-5 shadow-sm">
          <p className="flex items-center gap-2 font-heading text-sm font-semibold text-stone-900"><Plus size={16} /> Add one question</p>
          <div className="mt-3 flex gap-2">
            <Input placeholder="e.g. What do you miss most about...?" value={text} onChange={(e) => setText(e.target.value)}
              className="border-stone-300 bg-white" data-testid="add-question-input" />
            <Select value={theme} onValueChange={setTheme}>
              <SelectTrigger className="w-40 border-stone-300 bg-white" data-testid="add-question-theme"><SelectValue /></SelectTrigger>
              <SelectContent>{THEMES.map((t) => <SelectItem key={t} value={t}>{t}</SelectItem>)}</SelectContent>
            </Select>
          </div>
          <Button onClick={add} disabled={busy || !text.trim()} className="mt-3 bg-primary text-primary-foreground hover:bg-stone-900" data-testid="add-question-button">Add Question</Button>
        </div>
        <div className="rounded-lg border border-stone-200 bg-white p-5 shadow-sm">
          <p className="flex items-center gap-2 font-heading text-sm font-semibold text-stone-900"><ClipboardText size={16} /> Bulk add (one per line)</p>
          <Textarea rows={3} placeholder={"What did your mother teach you that you still carry?\nHow do you like to spend Sunday afternoons?"} value={bulk} onChange={(e) => setBulk(e.target.value)}
            className="mt-3 border-stone-300 bg-white text-sm" data-testid="bulk-add-textarea" />
          <Button onClick={bulkAdd} disabled={busy || !bulk.trim()} variant="outline" className="mt-3 border-stone-300 text-stone-700 hover:bg-stone-900 hover:text-white" data-testid="bulk-add-button">Add All</Button>
        </div>
      </div>

      {questions === null ? (
        <div className="flex justify-center py-20"><div className="h-8 w-8 animate-spin rounded-full border-2 border-primary border-t-transparent" /></div>
      ) : (
        <>
          <p className="mt-8 text-sm text-stone-500" data-testid="questions-count">
            <span className="font-semibold text-stone-900">{activeCount}</span> of {questions.length} questions active — active questions appear on every client's Person-Centered Survey.
          </p>
          <div className="mt-4 space-y-6">
            {grouped.map((g) => (
              <div key={g.theme} className="rounded-lg border border-stone-200 bg-white shadow-sm" data-testid={`theme-group-${g.theme.toLowerCase().replace(/[^a-z0-9]+/g, "-")}`}>
                <p className="flex items-center gap-2 border-b border-stone-200 bg-secondary/60 px-5 py-3 font-heading text-sm font-semibold text-stone-800">
                  <Books size={16} className="text-primary" /> {g.theme}
                </p>
                <div className="divide-y divide-stone-100">
                  {g.items.map((q) => (
                    <div key={q.id} className="flex items-center gap-3 px-5 py-3.5" data-testid={`question-row-${q.id}`}>
                      <Switch checked={q.active} onCheckedChange={() => toggle(q)} data-testid={`question-toggle-${q.id}`} />
                      <p className={`min-w-0 flex-1 text-sm ${q.active ? "text-stone-800" : "text-stone-400 line-through"}`}>{q.text}</p>
                      <Badge variant="outline" className={`shrink-0 border-0 ${SOURCE_STYLES[q.source] || SOURCE_STYLES.custom}`}>{q.source === "ai" ? "AI Suggested" : q.source === "library" ? "Library" : "Custom"}</Badge>
                      <button onClick={() => remove(q)} className="shrink-0 text-stone-300 transition-colors hover:text-[#C0392B]" data-testid={`question-delete-${q.id}`}>
                        <Trash size={15} />
                      </button>
                    </div>
                  ))}
                </div>
              </div>
            ))}
          </div>
        </>
      )}
    </div>
  );
}
